import React from "react";
import { styled } from "@mui/material/styles";
import { useTheme } from "@emotion/react";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import useMediaQuery from "@mui/material/useMediaQuery";

import IconButton from "@vapor/react-material/IconButton";
import CssBaseline from "@vapor/react-material/CssBaseline";
import Box from "@vapor/react-material/Box";
import { Drawer } from "./TempDrawer";
import List from "@vapor/react-material/List";
import ListItemButton from "@vapor/react-material/ListItemButton";
import ListItemIcon from "@vapor/react-material/ListItemIcon";
import ListItemText from "@vapor/react-material/ListItemText";
import Slide from "@mui/material/Slide";
import Popover from "@vapor/react-material/Popover";
import VaporHeaderBar from "@vapor/react-custom/VaporHeaderBar";
import { PopoverProps, SlideProps } from "@vapor/react-material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons/faArrowLeft";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons/faAngleRight";
import Bars from "@vapor/react-icons/Bars";

import { library } from "@fortawesome/fontawesome-svg-core";
import { far as proRegularFar } from "@fortawesome/pro-regular-svg-icons";
library.add(proRegularFar);

// const camelize = (s) => s.replace(/-./g, (x) => x[1].toUpperCase());

// === icons with React.lazy ===

// const LazyIcon = ({ icon }) => {
//   const Icon = React.lazy(() =>
//     import(`@fortawesome/pro-regular-svg-icons/${icon}.js`).then(
//       ({ definition }) => ({
//         default: () => (
//           <FontAwesomeIcon icon={definition} style={{ width: "16px" }} />
//         )
//       })
//     )
//   );

//   return <Icon />;
// };

// === icons with useEffect ===

// const LazyIcon = ({ icon }) => {
//   const [Icon, setIcon] = React.useState(null);

//   React.useEffect(() => {
//     import(`@fortawesome/pro-regular-svg-icons/${icon}.js`)
//       .then(({ definition }) => {
//         setIcon(() => () => (
//           <FontAwesomeIcon icon={definition} style={{ width: "16px" }} />
//         ));
//       })
//       .catch((error) => console.log(error));
//   }, [icon]);

//   if (!Icon) return null;

//   return <Icon />;
// };

type UIShellProps = {
  children?: React.ReactNode;
  renderToolbar?: React.ReactNode;
  renderDrawer?: React.ReactNode;
  drawerOpen?: boolean;
  setDrawerOpen?: Function;
  hideDrawer?: boolean;
  menuLevels?: Object | any;
};

export interface ExtendedPopoverProps extends PopoverProps {
  menuItem?: "menuItem";
}

const drawerWidth = 248;

const Transition = React.forwardRef(
  (props: SlideProps, ref: React.Ref<HTMLDivElement>) => {
    return (
      <Slide
        direction="right"
        easing={{
          enter: "transform 300ms cubic-bezier(0, 0, 0.2, 1) 0ms",
          exit: "transform 300ms cubic-bezier(0, 0, 0.2, 1) 0ms"
        }}
        ref={ref}
        {...props}
      />
    );
  }
);

const openedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: "hidden"
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(6)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(6)} + 1px)`
  }
});

const DrawerXl: (props) => JSX.Element = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open"
})<{ open?: boolean }>(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "none",

  ...((open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "248px !important"
    }
  }) as any),
  ...((!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .MuiDrawer-paperAnchorLeft": {
      width: "48px !important"
    }
  }) as any)
}));

export const VaporUIShellNav = (props: UIShellProps) => {
  const { renderToolbar, renderDrawer, hideDrawer } = props;
  const { drawerOpen, setDrawerOpen } = props;

  const theme: any = useTheme();
  const [selectedIndex, setSelectedIndex] = React.useState(null);

  const [sublevel, setSublevel] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  React.useEffect(() => {
    hideDrawer && props.setDrawerOpen(false);
  }, []);

  const handlePopoverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const openPopover = Boolean(anchorEl);

  const setNextLevel = (child) => {
    setSublevel(child);
  };

  const showUpperLevel = (child) => {
    setSublevel(child.parent?.parent ? child.parent : undefined);
  };

  const transformRec = (ob, parent = undefined) => {
    ob.parent = parent;

    if (ob?.children) {
      for (let i = 0; i < ob.children.length; i++) {
        transformRec(ob.children[i], ob);
      }
    }
    return ob;
  };

  if (props.menuLevels) {
    transformRec(props.menuLevels);
  }

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <Box>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {renderToolbar || null}

        <UiShellDrawer
          drawerOpen={drawerOpen}
          hideDrawer={hideDrawer}
          toggleDrawer={toggleDrawer}
          hasToolbar={renderToolbar === undefined ? false : true}
        >
          {renderDrawer || null}

          {props.menuLevels && (
            <List disablePadding>
              {props.menuLevels?.children?.map((child, childIndex) => {
                return (
                  <React.Fragment key={childIndex}>
                    <ListItemButton
                      selected={selectedIndex === childIndex}
                      onClick={(event) => {
                        if (child.children) {
                          setNextLevel(child);
                          handlePopoverClick(event);
                        }
                        setSelectedIndex(childIndex);
                        child.onClickFunction && child.onClickFunction();
                      }}
                      sx={{
                        pl: drawerOpen ? "16px" : "15px",
                        height: 48,
                        "&.MuiButtonBase-root.MuiListItemButton-root.Mui-selected":
                          {
                            border: `1px solid ${theme.palette.primary.primary} !important`
                          }
                      }}
                    >
                      <ListItemIcon variant="menuIcon">
                        {typeof child.icon === "string" &&
                        child.icon.startsWith("fa-") ? (
                          <FontAwesomeIcon
                            icon={["far", child.icon.substring(3)]}
                            // icon={["far", "user"]}
                            style={{ width: 16 }}
                          />
                        ) : (
                          // <LazyIcon icon={camelize(child.icon)} />
                          // <React.Suspense fallback={<div>Loading...</div>}>
                          //   <LazyIcon icon={camelize(child.icon)} />
                          //   {testIcons[child.icon]()}
                          //   <Icon />
                          // </React.Suspense>
                          child.icon || null
                        )}
                      </ListItemIcon>

                      <ListItemText
                        primary={child?.label}
                        sx={{
                          my: 0,
                          opacity: drawerOpen ? 1 : 0
                        }}
                      />
                      {child?.children && (
                        <FontAwesomeIcon
                          style={{ color: "white", width: 7, height: 12 }}
                          icon={faAngleRight}
                        />
                      )}
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </UiShellDrawer>

        {sublevel && (
          <Popover
            open={openPopover}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            PaperProps={{
              variant: "menuItem"
            }}
            BackdropProps={{
              invisible: true
            }}
            TransitionComponent={Transition}
          >
            <Box
              sx={{
                "&.MuiBox-root .MuiTypography-root.MuiTypography-titleSmall.MuiTypography-root":
                  {
                    display: "-webkit-box",
                    textOverflow: "ellipsis",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }
              }}
            >
              <VaporHeaderBar
                reduced
                size="small"
                title={sublevel?.label}
                divider
                leftItems={[
                  sublevel?.parent.parent !== undefined && (
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => showUpperLevel(sublevel)}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </IconButton>
                  )
                ]}
                rightItems={[
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => handlePopoverClose()}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </IconButton>
                ]}
              />
              <List disablePadding>
                {sublevel.children &&
                  sublevel.children.map((child: any, childIndex) => (
                    <React.Fragment key={childIndex}>
                      <ListItemButton
                        onClick={() => {
                          if (child.children) {
                            setNextLevel(child);
                          }
                          child.onClickFunction && child.onClickFunction();
                        }}
                        sx={{
                          height: 48,
                          pl: "14px",
                          "&:hover": {
                            border: "1px solid transparent !important",
                            background: `${theme.palette.primary.interactiveSelectedColor} !important`
                          }
                        }}
                      >
                        <ListItemText
                          primary={child?.label}
                          sx={{
                            my: 0,
                            "&.MuiListItemText-root .MuiTypography-root": {
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis"
                            }
                          }}
                        />
                        {child?.children && (
                          <FontAwesomeIcon
                            style={{
                              color: theme.palette.primary.iconDefault,
                              width: 7,
                              height: 12
                            }}
                            icon={faAngleRight}
                          />
                        )}
                      </ListItemButton>
                    </React.Fragment>
                  ))}
              </List>
            </Box>
          </Popover>
        )}

        <Box sx={{ flexGrow: 1, mt: renderToolbar ? "48px" : 0 }}>
          {props.children}
        </Box>
      </Box>
    </Box>
  );
};

type UiShellDrawerProps = {
  drawerOpen: boolean;
  hideDrawer: boolean;
  toggleDrawer: () => void;
  hasToolbar: boolean;
  children: React.ReactNode;
};

const UiShellDrawer = React.forwardRef<HTMLDivElement, UiShellDrawerProps>(
  ({ drawerOpen, hideDrawer, toggleDrawer, hasToolbar, children }, ref) => {
    const theme: any = useTheme();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

    return (
      <>
        {(isLarge || !hasToolbar) && (
          <DrawerXl
            variant="permanent"
            open={drawerOpen}
            sx={{
              display: hideDrawer
                ? "none"
                : { xs: !hasToolbar ? "block" : "none", lg: "block" },
              overflowY: "hidden"
            }}
            ref={ref}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: drawerOpen ? "flex-end" : "center",
                padding: theme.spacing(0, 1),
                height: 48
              }}
            >
              <IconButton
                onClick={toggleDrawer}
                sx={{
                  "&.MuiButtonBase-root svg": {
                    color: "white !important",
                    fontSize: "20px !important"
                  }
                }}
              >
                {drawerOpen ? <ChevronLeft /> : <Bars />}
              </IconButton>
            </Box>
            <Box
              sx={{
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: 0,
                  background: "transparent"
                }
              }}
            >
              <>{children}</>
            </Box>
          </DrawerXl>
        )}

        {!isLarge && hasToolbar && (
          <Drawer
            anchor="left"
            variant="temporary"
            ModalProps={{
              onBackdropClick: toggleDrawer,
              BackdropProps: {
                sx: { background: "rgba(0, 70, 102, 0.32) !important" }
              }
            }}
            sx={{
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                boxSizing: "border-box"
              },
              display: hideDrawer ? "none" : { xs: "block", lg: "none" },
              "& .MuiDrawer-paperAnchorLeft": {
                marginTop: "48px"
              },
              "& ::-webkit-scrollbar": {
                width: 0,
                background: "transparent"
              }
            }}
            open={drawerOpen}
            ref={ref}
          >
            <Box>{children}</Box>
          </Drawer>
        )}
      </>
    );
  }
);
